import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { CorrectionReason } from '@/lib/api/types';
import { AttributeRow } from './AttributeRow';
import { humanise, type AttributeTab, type AttributeView } from './review-model';

interface AttributeTabsProps {
  attributes: AttributeView[];
  assetKeys: string[];
  drafts: Record<string, string>;
  reasons: Record<string, CorrectionReason>;
  disabled: boolean;
  onDraft: (name: string, value: string | undefined) => void;
  onReason: (name: string, reason: CorrectionReason | undefined) => void;
}

const TABS: { id: AttributeTab; label: string }[] = [
  { id: 'identity', label: 'Identity' },
  { id: 'descriptions', label: 'Descriptions' },
];

/**
 * Identity and Descriptions, the two tabs of the Base44 review, each
 * listing every attribute of the item standard that belongs under it. The
 * required attributes still empty are pinned to the top of their tab, and
 * named above the tabs, so one sitting under the other tab is not missed.
 */
export function AttributeTabs({ attributes, assetKeys, drafts, reasons, disabled, onDraft, onReason }: AttributeTabsProps) {
  const [tab, setTab] = useState<AttributeTab>(() =>
    attributes.some((attribute) => attribute.tab === 'identity') ? 'identity' : 'descriptions',
  );
  const missing = attributes.filter((attribute) => attribute.missingRequired);
  const shown = attributes.filter((attribute) => attribute.tab === tab);

  return (
    <section aria-label="Attributes" className="flex flex-col gap-3">
      {missing.length > 0 && (
        <p className="rounded-card border border-warning p-3 text-sm text-warning">
          {missing.length === 1 ? 'One required field is empty' : `${missing.length} required fields are empty`}:{' '}
          {missing.map((attribute) => humanise(attribute.name)).join(', ')}. Fill{' '}
          {missing.length === 1 ? 'it' : 'them'} in below, or retake the frame that shows{' '}
          {missing.length === 1 ? 'it' : 'them'}.
        </p>
      )}

      <div role="tablist" aria-label="Attribute groups" className="flex border-b border-border">
        {TABS.map(({ id, label }) => {
          const inTab = attributes.filter((attribute) => attribute.tab === id);
          const empty = inTab.filter((attribute) => attribute.missingRequired).length;
          const active = tab === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              id={`tab-${id}`}
              aria-selected={active}
              aria-controls={`panel-${id}`}
              onClick={() => setTab(id)}
              className={cn(
                'flex min-h-11 flex-1 items-center justify-center gap-2 border-b-2 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                active ? 'border-accent text-surface-foreground' : 'border-transparent text-muted',
              )}
            >
              {label}
              <span className="text-xs text-muted">{inTab.length}</span>
              {empty > 0 && <span className="text-xs text-warning">{empty} empty</span>}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {shown.length === 0 ? (
          <p className="text-sm text-muted">Nothing in this group for this item.</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {shown.map((attribute) => (
              <AttributeRow
                key={attribute.name}
                attribute={attribute}
                assetKeys={assetKeys}
                draft={drafts[attribute.name]}
                reason={reasons[attribute.name]}
                disabled={disabled}
                onDraft={(value) => onDraft(attribute.name, value)}
                onReason={(reason) => onReason(attribute.name, reason)}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
